/**CORE LIBRARIES IMPORT */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**COMPONENTS IMPORT */
import TicketQr from "../Components/TicketQr";

/**DATA / HOOKS / SERVICES IMPORT */
import { formatEventDate } from "../data/events";
import usePageTitle from "../hooks/usePageTitle";
import { formatStartTime } from "../services/content";
import { viewTicket, getBookingMode } from "../services/booking";

const TOKEN_RE = /^gwt[2t]_[A-Za-z0-9_-]{43}$/;

/** Reads the ticket token from the link fragment (#t=...), never from the path or query. */
export function ticketFromHash(hash) {
  const m = String(hash || "").match(/^#t=([^&]+)$/);
  return m && TOKEN_RE.test(m[1]) ? m[1] : null;
}

/**
 * One guest's ticket, opened from its link (shared by the person who booked).
 * Shows the QR to present at the door. Holding the link is what grants access,
 * so the page shows nothing personal.
 */
const TicketView = () => {
  usePageTitle("Your ticket");
  const [token] = useState(() => ticketFromHash(window.location.hash));
  const [state, setState] = useState({ loading: Boolean(token), view: null, error: null });

  useEffect(() => {
    if (!token || getBookingMode() !== "backend") return;
    let cancelled = false;
    viewTicket(token)
      .then((view) => !cancelled && setState({ loading: false, view, error: null }))
      .catch(() => !cancelled && setState({ loading: false, view: null, error: "not_found" }));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const v = state.view;
  return (
    <main className="simple-page ticket-view">
      <p className="page-eyebrow">The Grey Wolf</p>
      <h1 className="page-title">{v?.test ? "Test ticket" : "Your ticket"}</h1>
      {!token || state.error ? (
        <p className="section-text" role="alert">
          This ticket link is not valid. Please ask the person who booked to share the ticket again, or{" "}
          <Link to="/ticket">resend the tickets</Link> to the booking email.
        </p>
      ) : state.loading || !v ? (
        <p className="section-text" role="status">Loading your ticket…</p>
      ) : (
        <>
          <TicketQr
            url={window.location.href.split("#")[0] + "#t=" + token}
            seq={v.ticket_seq}
            count={v.ticket_count}
            eventTitle={v.event_title}
            when={[formatEventDate(v.event_date), formatStartTime(v.start_time)].filter(Boolean).join(", ")}
            reference={v.test ? null : v.reference}
            test={v.test}
            status={v.status}
            amountDue={v.amount_due_cents}
          />
          <p className="details-note">
            Show this QR code at the door; staff scan it. Keep it to yourself: anyone holding it can use this place.
          </p>
        </>
      )}
    </main>
  );
};

export default TicketView;
